import { TAX_CODE_OTHER_EXEMPT, LineItemTypeOptions } from "../types/OcrTypes";
import { v4 as uuidv4 } from 'uuid';
import { areLineItemsEqual, valueToFixedNumber } from "./OcrInvoiceHelpers";
import { getCurrentLocalDateAsUtcIso } from '../utils/DateUtils';
// the way this is setup this must be called with immer. If you do not use immer, this will not work correctly.
export function ocrInvoiceReducer(draft, action) {
    switch (action.type) {
        case 'setInvoice': {
            const keys = Object.keys(draft);
            for (let i = 0; i < keys.length; i++) {
                const curKey = keys[i];
                if (action.invoice[curKey]) {
                    draft[curKey] = action.invoice[curKey];
                }
            }
            return draft;
        }
        case 'addLineItem': {
            draft.lineItems.unshift(getEmptyLineItem());
            break;
        }
        case 'removeLineItem': {
            draft.lineItems = draft.lineItems.filter((lineItem) => lineItem.reactKey !== action.lineItemKey);
            updateTotals(draft);
            break;
        }
        case 'updateLineItemValue': {
            const lineItem = getLineItemWithKey(draft, action.lineItemKey);
            if (lineItem) {
                lineItem.primaryItemData[action.lineItemValueKey] = action.value;
                if (action.lineItemValueKey === 'tax') {
                    lineItem.primaryItemData.taxIsSet = true;
                }
                else if (action.lineItemValueKey === 'taxCode' && action.value === TAX_CODE_OTHER_EXEMPT.value) {
                    lineItem.primaryItemData.tax = {
                        dollarAmount: 0,
                        percent: 0
                    };
                }
                updateLineItemTotal(lineItem);
                updateTotals(draft);
            }
            break;
        }
        case 'updateLineItem': {
            for (let i = 0; i < draft.lineItems.length; i++) {
                if (draft.lineItems[i].reactKey === action.lineItemKey) {
                    draft.lineItems[i] = action.value;
                    updateLineItemTotal(draft.lineItems[i]);
                    updateTotals(draft);
                    break;
                }
            }
            break;
        }
        case 'updateTopLevelDataValue': {
            draft.topLevelData[action.topLevelDataKey] = action.value;
            break;
        }
        case 'updateTopLevelData': {
            draft.topLevelData = action.value;
            break;
        }
    }
}
export function updateLineItemTotal(lineItem) {
    lineItem.primaryItemData.total = getLineItemTotal(lineItem);
    lineItem.matchResult = getLineItemMatchResult(lineItem);
    updateLineItemTax(lineItem, lineItem.primaryItemData.tax.percent / 100);
}
// TODO: CHECK: looks like this method is not used anywhere
export function updateLineItemTotalValue(lineItem) {
    lineItem.primaryItemData.total = getLineItemTotal(lineItem);
    lineItem.matchResult = getLineItemMatchResult(lineItem);
    updateLineItemTax(lineItem, lineItem.primaryItemData.tax.percent / 100);
    return lineItem;
}
function getLineItemWithKey(invoice, lineItemKey) {
    for (let i = 0; i < invoice.lineItems.length; i++) {
        if (invoice.lineItems[i].reactKey === lineItemKey) {
            return invoice.lineItems[i];
        }
    }
    return null;
}
/**
 * Get the subtotal with the discount subtracted
 */
function getSubtotalWithDiscounts(lineItem) {
    var _a;
    return getLineItemSubtotal(lineItem) - Math.abs(valueToFixedNumber((_a = lineItem === null || lineItem === void 0 ? void 0 : lineItem.primaryItemData) === null || _a === void 0 ? void 0 : _a.discount));
}
/**
 * Get the total for the line item (value after discounts and taxes are applied)
 */
function getLineItemTotal(ocrLineItem) {
    var _a;
    const lineItem = ocrLineItem.primaryItemData;
    const tax = ((_a = lineItem === null || lineItem === void 0 ? void 0 : lineItem.tax) === null || _a === void 0 ? void 0 : _a.dollarAmount) || 0;
    return valueToFixedNumber(getSubtotalWithDiscounts(ocrLineItem) + tax);
}
/**
 * Get the subtotal for the line item (total before discounts and tax are applied)
 */
function getLineItemSubtotal(ocrLineItem) {
    var _a, _b, _c, _d;
    const lineItem = ocrLineItem.primaryItemData;
    if (!lineItem) {
        return 0;
    }
    const quantity = parseFloat((_a = lineItem === null || lineItem === void 0 ? void 0 : lineItem.quantity) === null || _a === void 0 ? void 0 : _a.toString()) || 0;
    const weight = parseFloat((_b = lineItem === null || lineItem === void 0 ? void 0 : lineItem.weight) === null || _b === void 0 ? void 0 : _b.toString()) || 0;
    const displayedUnitPrice = valueToFixedNumber((_c = ocrLineItem === null || ocrLineItem === void 0 ? void 0 : ocrLineItem.primaryItemData) === null || _c === void 0 ? void 0 : _c.unitPrice);
    let weightAdjustedUnitPrice = displayedUnitPrice;
    if ((_d = ocrLineItem === null || ocrLineItem === void 0 ? void 0 : ocrLineItem.linkedCatalogData) === null || _d === void 0 ? void 0 : _d.quantity) {
        weightAdjustedUnitPrice /= ocrLineItem.linkedCatalogData.quantity;
    }
    weightAdjustedUnitPrice = valueToFixedNumber(weightAdjustedUnitPrice);
    const { hasFinalSalesQuantitySet } = lineItem;
    const isWeightDifferentThanOriginal = lineItem.weight !== lineItem.originalWeight;
    // if this order line item has had its finalSalesQuantity set then we ignore the quantity field entirely.
    // Further, if the weight isn't the original weight then we ignore quantity. The reason being when we send this to
    // be-core it will set the finalSalesQuantity value which will ignore quantity in the totals. We want the totals
    // displayed to reflect what will happen in be-core.
    if ((hasFinalSalesQuantitySet || isWeightDifferentThanOriginal) && weight > 0) {
        return weightAdjustedUnitPrice * weight;
    }
    return valueToFixedNumber(displayedUnitPrice * quantity);
}
function updateLineItemTax(lineItem, globalTaxPercent) {
    lineItem.primaryItemData.tax.percent = globalTaxPercent * 100;
    const curSubtotal = getSubtotalWithDiscounts(lineItem);
    const newDollarTax = valueToFixedNumber(curSubtotal * (globalTaxPercent));
    lineItem.primaryItemData.tax.dollarAmount = newDollarTax;
    lineItem.primaryItemData.total = getLineItemTotal(lineItem);
    lineItem.matchResult = getLineItemMatchResult(lineItem);
}
/**
 * Calculate the math result for a line item.
 * @param lineItem
 * @returns
 */
function getLineItemMatchResult(lineItem) {
    const isEqualToDefault = areLineItemsEqual(lineItem.primaryItemData, lineItem.defaultLineItem);
    let { matchResult } = lineItem;
    if (!isEqualToDefault && lineItem.defaultMatchResult !== 'new' && lineItem.defaultMatchResult !== 'onlyOcr') {
        matchResult = 'userChange';
    }
    else if (matchResult === 'userChange') {
        matchResult = lineItem.defaultMatchResult;
    }
    return matchResult;
}
// TODO: CHECK: looks like this method is not used anywhere
export function updateInvoiceTotals(invoice) {
    const newInvoice = Object.assign({}, invoice);
    updateTotals(newInvoice);
    return newInvoice;
}
/**
 * Updates the top level totals of the invoices
 * @param draft
 */
export function updateTotals(draft) {
    var _a, _b;
    const lineItems = draft.lineItems;
    let newSubtotal = 0;
    let newTax = 0;
    let newDiscount = 0;
    for (let i = 0; i < lineItems.length; i++) {
        const curLineItem = lineItems[i];
        if (curLineItem.isRemoved) {
            continue;
        }
        const curTotal = valueToFixedNumber(curLineItem === null || curLineItem === void 0 ? void 0 : curLineItem.primaryItemData.total) || 0;
        const curTax = valueToFixedNumber((_b = (_a = curLineItem === null || curLineItem === void 0 ? void 0 : curLineItem.primaryItemData) === null || _a === void 0 ? void 0 : _a.tax) === null || _b === void 0 ? void 0 : _b.dollarAmount) || 0;
        const curDiscount = valueToFixedNumber(curLineItem === null || curLineItem === void 0 ? void 0 : curLineItem.primaryItemData.discount);
        const curSubtotal = curTotal + curDiscount - curTax;
        newSubtotal += curSubtotal;
        newTax += curTax;
        newDiscount += curDiscount;
    }
    const newTopLevelValues = Object.assign({}, draft.topLevelData);
    newTopLevelValues.subtotal = valueToFixedNumber(newSubtotal);
    newTopLevelValues.tax = valueToFixedNumber(newTax);
    newTopLevelValues.total = valueToFixedNumber(newSubtotal - newDiscount + valueToFixedNumber(newTax || 0));
    newTopLevelValues.totalDiscount = newDiscount;
    draft.topLevelData = newTopLevelValues;
    // return {...draft, ...{topLevelData: newTopLevelValues, lineItems: draft.lineItems}};
}
const baseLineItemData = {
    name: '',
    sku: '',
    unit: '',
    quantity: 0,
    unitPrice: 0,
    weight: 0,
    taxCode: '99999',
    taxable: false,
    total: 0,
    tax: { dollarAmount: 0, percent: 0 },
};
const emptyLineItemData = (type = LineItemTypeOptions.PRODUCT) => {
    switch (type) {
        case LineItemTypeOptions.PRODUCT:
            return Object.assign({}, baseLineItemData);
        case LineItemTypeOptions.TAX:
            return Object.assign(Object.assign({}, baseLineItemData), { unit: 'case', quantity: 1, weight: 1 });
        case LineItemTypeOptions.FEE:
            return Object.assign(Object.assign({}, baseLineItemData), { unit: 'case', quantity: 1, weight: 1 });
    }
};
/**
 * ? TODO: CHECK: looks like this method is useless, we have BaseOcrDataPageFunctions.makeEmptyLineItem
 * Returns a placeholder line item
 * @returns
 */
export function getEmptyLineItem(type = LineItemTypeOptions.PRODUCT) {
    return {
        primaryItemData: Object.assign({}, emptyLineItemData(type)),
        secondaryItemData: Object.assign({}, emptyLineItemData(type)),
        matchResult: 'new',
        isRemoved: false,
        reactKey: uuidv4(),
        defaultLineItem: Object.assign({}, emptyLineItemData(type)),
        type,
        confidence: {
            name: 1,
            sku: 1,
            unit: 1,
            quantity: 1,
            unitPrice: 1,
            weight: 1,
        },
    };
}
/**
 * Returns a placeholder top level data
 * @returns
 */
export function getEmptyTopLevelData() {
    return {
        invoiceNumber: '',
        supplierName: '',
        externalReferenceID: 'string',
        buyerName: '',
        issueDate: getCurrentLocalDateAsUtcIso(),
        dueDate: getCurrentLocalDateAsUtcIso(),
        subtotal: 0,
        tax: 0,
        total: 0,
        totalDiscount: 0,
        accountingClassID: '',
        accountingVendorID: null,
        confidence: {
            invoiceNumber: 1,
            externalReferenceID: 1,
            issueDate: 1,
            dueDate: 1,
            totalDiscount: 1,
            subtotal: 1,
            tax: 1,
            total: 1,
        }
    };
}
/**
 * Returns a placeholder empty invoice
 * @returns
 */
export function getEmptyInvoice() {
    return {
        topLevelData: getEmptyTopLevelData(),
        lineItems: [],
        chartOfAccountDetails: [],
        topLevelDataOrder: getEmptyTopLevelData(),
        invoiceID: '',
        glCodes: [],
        accountingClasses: [],
        rawInvoiceData: null,
        lowConfidenceFields: {
            errorFields: [],
            totalCount: 0,
        },
        missingRequiredFields: {
            errorFields: [],
            totalCount: 0,
        },
        catalogPriceListID: null,
        catalogID: null,
        accountingVendors: [],
    };
}
/**
 * Returns a placeholder
 * @deprecated?
 * This function is not used in any place.
 */
export function getEmptyPrimaryLineItem() {
    return Object.assign(Object.assign({}, emptyLineItemData(LineItemTypeOptions.PRODUCT)), { reactKey: uuidv4() });
}
export function createNewProductLineItems(lineItems) {
    return lineItems.map((lineItem) => {
        var _a, _b, _c;
        return (Object.assign(Object.assign({}, lineItem.primaryItemData), { availability: 'IN_STOCK', description: (_a = lineItem === null || lineItem === void 0 ? void 0 : lineItem.primaryItemData) === null || _a === void 0 ? void 0 : _a.name, category: '', reactKey: lineItem === null || lineItem === void 0 ? void 0 : lineItem.reactKey, catalogPrice: (_c = (_b = lineItem === null || lineItem === void 0 ? void 0 : lineItem.primaryItemData) === null || _b === void 0 ? void 0 : _b.unitPrice) === null || _c === void 0 ? void 0 : _c.toString(), catalogQuantity: '1' }));
    });
}
export function createCatalogProduct({ product, catalogID, catalogPriceListID }) {
    return {
        id: '',
        name: product.name,
        description: product.description,
        taxCode: product.taxCode,
        supplierCatalogId: catalogID,
        category: product.category,
        packages: [{
                description: '',
                quantity: Number(product.catalogQuantity),
                unit: product.unit,
                sku: product.sku,
                availability: product.availability,
                isNew: true,
                prices: [{
                        pricePerUnit: Number(product.catalogPrice),
                        isNew: true,
                        priceListID: catalogPriceListID,
                    }],
            }],
    };
}
//# sourceMappingURL=OcrInvoiceReducer.js.map